import React, {useEffect} from 'react';
import { observer } from 'mobx-react';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';

import styles from './dashboard.module.css';
import dashboardStore from '../../stores/dashboard.store';
import memberStore from '../../stores/member.store';
import bridgeLayoutStore from '../../stores/bridge_layout.store';
import loglyticsStore from '../../stores/loglytics.store';
import Time from '../../services/time.service';
import { RangeMode } from '../../models/search.model';
import { Severity } from '../../models/log.model';
import Select from '../common/select.component';
import TimeRangeButtons from '../common/time_range_buttons.component';
import BridgeWidgetCard from './bridge_widget_card.component';

const Dashboard = observer(() => {
  const { app } = memberStore;
  useEffect(() => {
    if (app == null) return;
    loglyticsStore.setSeverity(Severity.ERROR);
    dashboardStore.setRange(RangeMode.HOUR);
  }, [app]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  function changeVersion(version: string) {
    dashboardStore.setVersion(version);
  }

  function changeRange(range: RangeMode) {
    dashboardStore.setRange(range);
  }

  function dragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    bridgeLayoutStore.move(String(active.id), String(over.id));
  }

  const { logSums, range, version, appVersions } = dashboardStore;
  const { layout, editing } = bridgeLayoutStore;

  const logData = logSums.slice();
  let dateSpan = new Time().getLongDate();
  if (!!logData && logData.length > 0) {
    const startTime = new Time(logData[0].time);
    const endTime = new Time(logData[logData.length - 1].time);
    dateSpan = `${startTime.getLongDate()} ${startTime.getShortTime()} - ${endTime.getLongDate()} ${endTime.getShortTime()}`;
  }

  const versionNameValues: Record<string, string> = {'': 'All'};
  appVersions.forEach((vers) => versionNameValues[vers] = vers);

  const widgets = editing ? layout : layout.filter(w => w.visible);

  return (
    <div className={styles.component}>
      <div className={styles.header}>
        <div className={styles.versionLabel}>Time range:</div>
        <TimeRangeButtons value={range} onChange={changeRange} />

        <div className={styles.dateSpan}>{dateSpan}</div>
        <div className={styles.versionLabel}>Version:</div>

        <Select
          value={version}
          nameValues={versionNameValues}
          onChange={changeVersion}/>
        <button className={styles.editButton} onClick={() => bridgeLayoutStore.setEditing(!editing)}>
          {editing ? 'Done' : 'Edit layout'}
        </button>
      </div>
      <div className={styles.body}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={dragEnd}>
          <SortableContext items={widgets.map(w => w.id)} strategy={rectSortingStrategy}>
            <div className={styles.grid}>
              {widgets.map(w => <BridgeWidgetCard key={w.id} layout={w} editing={editing} />)}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
});

export default Dashboard;
